import { Suspense, lazy, memo, useState } from 'react';

// Lazy load PDF thumbnail component to reduce initial bundle size
const LazyPdfThumbnail = lazy(() =>
  import('./PdfThumbnail')
    .then(module => {
      if (!module.PdfThumbnail) {
        throw new Error('PdfThumbnail component not found in module');
      }
      return { default: module.PdfThumbnail };
    })
    .catch(error => {
      console.error('[PdfThumbnailWrapper] Failed to lazy load PdfThumbnail:', error);
      throw error;
    }),
);

export interface PdfThumbnailWrapperProps {
  fileUrl: string;
  fallbackIcon: React.ReactNode;
  className?: string;
  loadingClassName?: string;
  width?: number;
  height?: number;
}

/**
 * Wrapper component for PDF thumbnails that handles lazy loading and error fallback
 */
export const PdfThumbnailWrapper: React.FC<PdfThumbnailWrapperProps> = memo(
  ({ fileUrl, fallbackIcon, className, loadingClassName, width = 100, height = 107 }) => {
    const [hasError, setHasError] = useState(false);

    if (hasError) {
      return (
        <div className={className} title="PDF preview error">
          {fallbackIcon}
        </div>
      );
    }

    return (
      <Suspense fallback={<div className={loadingClassName || className}>{fallbackIcon}</div>}>
        <LazyPdfThumbnail
          fileUrl={fileUrl}
          width={width}
          height={height}
          className={className}
          onError={() => setHasError(true)}
        />
      </Suspense>
    );
  },
);

PdfThumbnailWrapper.displayName = 'PdfThumbnailWrapper';
